import type {SerializablePlainData} from '../../../../history/domain/SerializablePlainData';
import {
    createSketchSolverIslandId,
    resolveSolverIsland,
    sortUniqueStrings,
    type SketchConstraintState,
    type SketchConstraintTargetRef,
    type SketchDimensionState,
    type SketchEdgeState,
    type SketchPointState,
    type SketchRelationRuntimeState,
    type SketchSolverIslandSnapshot,
    type SketchSolveStatus,
    type SolverParameterEntry,
    type SolverParameterVector,
} from '../../../../history/solver';
import {isSketchMirrorAxisReferenceProvider, isSketchPointObject} from '../../../../mesh/guards';
import type {ISketchObject, SketchPointObject} from '../../../../mesh/sketch';
import type {SketchRelationDefinition} from '../relationDefinition';
import {readSketchRelationNumericValue} from '../relation-compatibility/SketchRelationDefinitions';
import {SketchRelationKind} from '../relation-compatibility/SketchRelationKinds';
import type {SketchSolverRuntimeGraph} from './SketchSolverObjectCollector';
import {
    collectSketchSolverRuntimeGraph,
    isRuntimeSketchEdge,
    readSketchEntityPointIds,
    resolveSketchRelationDefinitionHistoryId,
} from './SketchSolverObjectCollector';
import type {
    CreateSketchSolverRuntimeSnapshotInput,
    SketchSolverRuntimeSnapshotResult,
} from './SketchSolverRuntimeTypes';

/**
 * 默认求解状态。
 */
const DEFAULT_SOLVE_STATUS: SketchSolveStatus = 'unknown';

/**
 * 判断值是否为有限数。
 *
 * @param value 待判断值。
 * @returns 是否为有限数。
 */
function isFiniteNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

/**
 * 判断值是否为 plain object。
 *
 * @param value 待判断值。
 * @returns 是否为 plain object。
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }

    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}

/**
 * 将运行时值转换为可序列化 plain data。
 *
 * 说明：
 * 1. Three.js 向量会被压平成 number[]；
 * 2. 函数、class 实例、Map、Set、Object3D 等运行时对象会被过滤；
 * 3. 深度限制用于避免错误对象造成递归爆炸。
 *
 * @param value 运行时值。
 * @param depth 当前深度。
 * @returns 可序列化数据；无法转换时返回 undefined。
 */
function toSerializablePlainData(value: unknown, depth = 0): SerializablePlainData | undefined {
    if (depth > 8) {
        return undefined;
    }

    if (value === null || typeof value === 'string' || typeof value === 'boolean') {
        return value as SerializablePlainData;
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined;
    }

    if (Array.isArray(value)) {
        const items = value
            .map((item) => toSerializablePlainData(item, depth + 1))
            .filter((item): item is SerializablePlainData => item !== undefined);
        return items;
    }

    if (value && typeof value === 'object') {
        const toArray = Reflect.get(value, 'toArray');
        if (typeof toArray === 'function') {
            const arrayValue = toArray.call(value);
            if (Array.isArray(arrayValue) && arrayValue.every((item) => typeof item === 'number')) {
                return arrayValue.filter((item): item is number => typeof item === 'number' && Number.isFinite(item));
            }
        }
    }

    if (!isPlainObject(value)) {
        return undefined;
    }

    const record: Record<string, SerializablePlainData> = {};
    Object.entries(value).forEach(([key, item]) => {
        const serialized = toSerializablePlainData(item, depth + 1);
        if (serialized !== undefined) {
            record[key] = serialized;
        }
    });

    return record;
}

/**
 * 将运行时值转换为 plain record。
 *
 * @param value 运行时值。
 * @returns plain record。
 */
function toSerializablePlainRecord(value: unknown): Readonly<Record<string, SerializablePlainData>> {
    const serialized = toSerializablePlainData(value);
    return isPlainObject(serialized) ? serialized : {};
}

/**
 * 读取点角色。
 *
 * @param point 草图点。
 * @returns 点角色。
 */
function readPointRole(point: SketchPointObject): string | undefined {
    const roleKeys = [
        'pointKind',
        'slotControlPointRole',
        'ellipseControlPointRole',
        'parabolaControlPointRole',
    ];
    const roles = roleKeys
        .map((key) => Reflect.get(point, key))
        .filter((value) => value !== null && value !== undefined)
        .map(String);

    // 中点与象限点是实体刷新后可确定重建的派生控制点；快照必须显式保留该语义，
    // 不能把它们伪装成普通自由控制点，否则参数写回幂等检查无法区分定义量与显示派生量。
    if (point.isMidPoint) {
        roles.push('midPoint');
    }
    if (point.isQuadrantPoint) {
        roles.push('quadrantPoint');
    }

    return roles.length > 0 ? Array.from(new Set(roles)).join('/') : undefined;
}

/**
 * 读取点的唯一归属实体。
 *
 * @param pointId 点 ID。
 * @param graph 运行时图。
 * @returns 唯一实体 ID；多归属或无归属时返回 undefined。
 */
function readSinglePointOwnerEntityId(
    pointId: string,
    graph: SketchSolverRuntimeGraph,
): string | undefined {
    const ownerIds = graph.entities
        .filter((entity) => (entity.pointIds ?? []).includes(pointId))
        .map((entity) => entity.entityId);

    return ownerIds.length === 1 ? ownerIds[0] : undefined;
}

/**
 * 创建草图点状态。
 *
 * @param point 草图点。
 * @param graph 运行时图。
 * @returns 点状态。
 */
function createPointState(
    point: SketchPointObject,
    graph: SketchSolverRuntimeGraph,
): SketchPointState {
    const local = point.getLocalPosition2D();
    const world = point.getPosition();
    const isFixed = Reflect.get(point, 'isFixed');

    return {
        pointId: point.uuid,
        entityId: readSinglePointOwnerEntityId(point.uuid, graph),
        role: readPointRole(point),
        localPosition: [local.x, local.y],
        worldPosition: [world.x, world.y, world.z],
        fixed: typeof isFixed === 'function' ? Boolean(isFixed.call(point)) : undefined,
    };
}

/**
 * 读取实体构造线状态。
 *
 * @param entity 草图实体。
 * @returns 是否为构造线。
 */
function readEntityConstructionState(entity: ISketchObject): boolean | undefined {
    const construction = Reflect.get(entity, 'isConstruction');
    if (typeof construction === 'boolean') {
        return construction;
    }

    const auxiliary = Reflect.get(entity, 'isAuxiliaryObject');
    return typeof auxiliary === 'boolean' ? auxiliary : undefined;
}

/**
 * 读取实体圆心点 ID。
 *
 * 说明：
 * 1. 圆的圆心访问器是 `getCenterControlPoint`、圆弧/椭圆是 `getCenterPoint`，按存在性取用；
 * 2. 身份空间必须与 `pointIds`/`slotStructure` 一致，取运行时 `uuid`（不是持久 ID），
 *    否则构建器按点集查句柄必然落空。
 *
 * @param entity 草图实体。
 * @returns 圆心点运行时 ID；不适用时返回 null。
 */
function readEntityCenterPointId(entity: ISketchObject): string | null {
    for (const accessorName of ['getCenterControlPoint', 'getCenterPoint'] as const) {
        const accessor = Reflect.get(entity, accessorName);

        if (typeof accessor !== 'function') {
            continue;
        }

        const uuid = Reflect.get(accessor.call(entity) as object ?? {}, 'uuid');

        if (typeof uuid === 'string' && uuid.length > 0) {
            return uuid;
        }
    }

    return null;
}

/**
 * 创建实体几何快照。
 *
 * @param entity 草图实体。
 * @param pointIds 实体关联点 ID。
 * @returns 可序列化几何快照。
 */
function createEntityGeometrySnapshot(
    entity: ISketchObject,
    pointIds: readonly string[],
): Readonly<Record<string, SerializablePlainData>> {
    const geometry: Record<string, SerializablePlainData> = {
        objectType: String(entity.objectType),
        pointIds: [...pointIds],
    };
    const runtimeName = Reflect.get(entity, 'name');
    if (typeof runtimeName === 'string' && runtimeName.length > 0) {
        geometry.name = runtimeName;
    }

    const radius = Reflect.get(entity, 'radius');
    if (isFiniteNumber(radius)) {
        geometry.radius = radius;
    }

    const geometryData = toSerializablePlainData(Reflect.get(entity, 'geometryData'));
    if (geometryData !== undefined) {
        geometry.geometryData = geometryData;
    }

    const getArcDefinition = Reflect.get(entity, 'getArcDefinition');
    if (typeof getArcDefinition === 'function') {
        geometry.arcDefinition = toSerializablePlainRecord(getArcDefinition.call(entity));
    }

    const getEllipseDefinition = Reflect.get(entity, 'getEllipseDefinition');
    if (typeof getEllipseDefinition === 'function') {
        geometry.ellipseDefinition = toSerializablePlainRecord(getEllipseDefinition.call(entity));
    }

    const getParabolaDefinition = Reflect.get(entity, 'getParabolaDefinition');
    if (typeof getParabolaDefinition === 'function') {
        geometry.parabolaDefinition = toSerializablePlainRecord(getParabolaDefinition.call(entity));
    }

    /** 槽口本体没有成员边的 `closedCollection` 回链，需从首条成员边读取同一结构声明。 */
    if (Reflect.get(entity, 'isSketchSlotObject') === true) {
        const slotEdges = Reflect.get(entity, 'getEdges');
        const firstEdge = typeof slotEdges === 'function'
            ? (slotEdges.call(entity) as ISketchObject[])[0]
            : undefined;
        if (firstEdge) {
            const directSlotStructure = createStraightSlotStructureSnapshot(firstEdge);
            const directArcSlotStructure = createArcSlotStructureSnapshot(firstEdge);
            if (directSlotStructure) {
                geometry.slotStructure = directSlotStructure;
            }
            if (directArcSlotStructure) {
                geometry.arcSlotStructure = directArcSlotStructure;
            }
        }
    }

    const slotStructure = createStraightSlotStructureSnapshot(entity);
    if (slotStructure) {
        geometry.slotStructure = slotStructure;
    }

    const arcSlotStructure = createArcSlotStructureSnapshot(entity);
    if (arcSlotStructure) {
        geometry.arcSlotStructure = arcSlotStructure;
    }

    const splineStructure = createSplineStructureSnapshot(entity);
    if (splineStructure) {
        geometry.splineStructure = splineStructure;
    }

    /**
     * 圆/圆弧的圆心点 ID。
     *
     * 说明：`pointIds` 是排序去重的关联点集，无法区分圆心与圆周点；而相切等
     * 「线到圆心距离 = 半径」类方程必须拿到圆心句柄，因此在快照里显式声明该结构角色
     * （与 `slotStructure` 同范式：结构信息由采集侧声明，求解侧不做几何猜测）。
     */
    const centerPointId = readEntityCenterPointId(entity);
    if (centerPointId) {
        geometry.centerPointId = centerPointId;
    }

    /**
     * 圆弧起/终点 ID（与圆心同范式的结构角色声明）。
     *
     * 弧长参与求解（路径长度含弧段）时，扫角必须由**起点/终点**位置反算——
     * `pointIds` 无序且含中点，位置猜测在端点靠近时会翻车，故由采集侧声明。
     * 身份空间同 `centerPointId`：运行时 uuid。
     */
    for (const [key, accessorName, propertyName] of [
        ['startPointId', 'getStartPoint', 'startPoint'],
        ['endPointId', 'getEndPoint', 'endPoint'],
    ] as const) {
        const accessor = Reflect.get(entity, accessorName);
        /**
         * 圆弧公开 getStartPoint/getEndPoint，直线则以 startPoint/endPoint 属性暴露端点。
         * 两种实体必须落入同一有序角色协议，避免角度关系因 UUID 排序反转 π。
         */
        const endpoint = typeof accessor === 'function'
            ? accessor.call(entity)
            : Reflect.get(entity, propertyName);
        const uuid = Reflect.get(endpoint as object ?? {}, 'uuid');

        if (typeof uuid === 'string' && uuid.length > 0) {
            geometry[key] = uuid;
        }
    }

    return geometry;
}

/**
 * 采集样条结构角色（供参数化求解声明样条自由度）。
 *
 * 设计说明：
 * 1. 样条的采样曲线由 Worker 内的 Puppy CAD 内核插值算出（异步），求解器无法在残差里同步复算，
 *    因此这里把**有序控制点**与**当前采样弧长**一并声明：弧长对相似变换是一次齐次的，
 *    「整体缩放 s 倍 → 弧长恰好 s 倍」是精确关系，不需要复算曲线即可驱动总长；
 * 2. `pointIds` 是排序去重的无序点集，拿不到控制点顺序，故必须显式输出；
 * 3. 弧长按采样折线求和——这正是渲染与测量共用的那条曲线，二者口径一致。
 *
 * @param entity 草图实体。
 * @returns 结构快照；非样条或控制点不足返回 null。
 */
function createSplineStructureSnapshot(
    entity: ISketchObject,
): Readonly<Record<string, SerializablePlainData>> | null {
    const readControlPoints = Reflect.get(entity, 'getControlPoints');
    const readCurvePoints = Reflect.get(entity, 'getCurvePoints');
    const readCurveMetadata = Reflect.get(entity, 'getCurveMetadata');
    const readCurveEvaluationDefinition = Reflect.get(entity, 'getCurveEvaluationDefinition');

    if (typeof readControlPoints !== 'function' || typeof readCurvePoints !== 'function') {
        return null;
    }

    const controlPoints = readControlPoints.call(entity) as {uuid?: unknown}[];
    const curvePoints = readCurvePoints.call(entity) as {x: number; y: number; z: number}[];

    if (!Array.isArray(controlPoints) || controlPoints.length < 2
        || !Array.isArray(curvePoints) || curvePoints.length < 2) {
        return null;
    }

    const controlPointIds = controlPoints.map((point) => String(point.uuid ?? ''));
    const variant = Reflect.get(entity, 'isSketchStyleSplineObject') === true
        ? 'style'
        : Reflect.get(entity, 'isSketchSplineObject') === true
            ? 'standard'
            : null;

    if (!variant || controlPointIds.some((pointId) => pointId.length === 0)) {
        return null;
    }

    let sampledLength = 0;

    for (let index = 1; index < curvePoints.length; index += 1) {
        const previous = curvePoints[index - 1]!;
        const current = curvePoints[index]!;

        sampledLength += Math.hypot(current.x - previous.x, current.y - previous.y, current.z - previous.z);
    }

    if (sampledLength <= 0) {
        return null;
    }

    const structure: Record<string, SerializablePlainData> = {variant, controlPointIds, sampledLength};

    /**
     * Bezier / 显式 B 样条可由当前控制点参数同步精确求值；开放插值样条还需保存
     * Puppy CAD 内核导出的极点、权重和节点定义。两者都以 plain data 进入 island，
     * 公式层禁止再读取运行时对象或显示采样折线。
     */
    if (typeof readCurveMetadata === 'function') {
        structure.curveMetadata = toSerializablePlainRecord(readCurveMetadata.call(entity));
    }
    if (typeof readCurveEvaluationDefinition === 'function') {
        const evaluationDefinition = readCurveEvaluationDefinition.call(entity);

        if (evaluationDefinition) {
            const referencePlane = Reflect.get(entity, 'referencePlane');
            const worldToLocal = referencePlane && typeof referencePlane === 'object'
                ? Reflect.get(referencePlane, 'worldToLocal')
                : null;
            const controlPoints = Reflect.get(evaluationDefinition, 'controlPoints');

            if (typeof worldToLocal === 'function' && Array.isArray(controlPoints)) {
                /**
                 * 内核导出的极点是世界坐标；公式系统只允许草图局部二维坐标。
                 * 在采集边界完成转换，避免数值核心依赖 ReferencePlane 或 Three.js。
                 */
                structure.curveEvaluationDefinition = toSerializablePlainRecord({
                    ...evaluationDefinition,
                    controlPoints: controlPoints.map((point) => {
                        const local = worldToLocal.call(referencePlane, point);

                        return {x: local.x, y: local.y, z: 0};
                    }),
                });
            }
        }
    }

    return structure;
}

/**
 * 采集直槽口结构角色（供参数化求解声明槽口自由度）。
 *
 * 设计说明：
 * 1. 槽口本体与成员边都可能进入 `edgeStates`，因此两者使用同一结构声明，
 *    构建器按 `slotId` 去重消费；
 * 2. `pointIds` 只给无序点集，无法区分哪个是 p1/p2/p3/p4 与轴端点，
 *    而槽口耦合方程必须按角色绑定，所以这里显式输出有序角色；
 * 3. 圆弧槽口的参数化需要弧心/扫角联立，属另一条路径，这里只处理直槽口。
 *
 * @param entity 草图实体（成员边）。
 * @returns 结构快照；非直槽口成员返回 null。
 */
function createStraightSlotStructureSnapshot(
    entity: ISketchObject,
): Readonly<Record<string, SerializablePlainData>> | null {
    const owner = Reflect.get(entity, 'closedCollection');

    if (!owner || typeof owner !== 'object' || Reflect.get(owner, 'isSketchSlotObject') !== true) {
        return null;
    }

    const slot = owner as {
        uuid: string;
        isArcSlot: () => boolean;
        junctionPoints: {
            p1: {uuid: string; getLocalPosition2D: () => {x: number; y: number}};
            p2: {uuid: string};
            p3: {uuid: string};
            p4: {uuid: string};
        };
        centerlineStartPoint: {uuid: string; getLocalPosition2D: () => {x: number; y: number}} | null;
        centerlineEndPoint: {uuid: string; getLocalPosition2D: () => {x: number; y: number}} | null;
        centerPoint: {uuid: string} | null;
        startCapArc: {uuid: string; getCenterPoint: () => {uuid: string}; getArcDefinition: () => {radius: number}};
        endCapArc: {uuid: string; getCenterPoint: () => {uuid: string}};
    };

    if (slot.isArcSlot() || !slot.centerlineStartPoint || !slot.centerlineEndPoint) {
        return null;
    }

    const junction = slot.junctionPoints;
    const axisStart = slot.centerlineStartPoint.getLocalPosition2D();
    const axisEnd = slot.centerlineEndPoint.getLocalPosition2D();
    const firstJunction = junction.p1.getLocalPosition2D();
    const sideCross = (axisEnd.x - axisStart.x) * (firstJunction.y - axisStart.y)
        - (axisEnd.y - axisStart.y) * (firstJunction.x - axisStart.x);

    if (Math.abs(sideCross) <= 1e-12) {
        return null;
    }

    return {
        slotId: slot.uuid,
        junctionPointIds: [junction.p1.uuid, junction.p2.uuid, junction.p3.uuid, junction.p4.uuid],
        axisStartPointId: slot.centerlineStartPoint.uuid,
        axisEndPointId: slot.centerlineEndPoint.uuid,
        // 反射会翻转“轴方向 × P1 所在侧”的手性；结构方程必须保留该正式符号。
        normalSign: sideCross > 0 ? 1 : -1,
        // 端帽圆心与轴端点是**不同对象、同位置**，必须各自绑定，否则端帽不跟随。
        startCapCenterPointId: slot.startCapArc.getCenterPoint().uuid,
        endCapCenterPointId: slot.endCapArc.getCenterPoint().uuid,
        /**
         * 槽口中心控制点：轴中点的从动点。
         *
         * 漏声明的话它在求解里是自由点、原地不动，而轴端点会被尺寸挪走——
         * 中心点就此偏离真中点，且偏差随每次求解累积；下一次拖拽又以它为基准，
         * 表现为"松手时槽口整体挪一下"。
         */
        centerPointId: slot.centerPoint?.uuid ?? null,
        /**
         * 端帽弧实体 ID：端帽半径与槽口半宽是**同一个物理量**，
         * 求解器必须把二者绑成等式，否则半径参数无人约束、停在旧值，
         * 写回时又按旧半径把接合点吸附回去（宽度尺寸改了却弹回原值）。
         */
        startCapArcId: slot.startCapArc.uuid,
        endCapArcId: slot.endCapArc.uuid,
        halfWidth: slot.startCapArc.getArcDefinition().radius,
    };
}

/**
 * 采集圆弧槽口结构角色（供参数化求解声明圆弧槽口自由度）。
 *
 * 设计说明：
 * 1. 圆弧槽口的真实自由度是「弧心 C(2) + 中心弧半径 R + 半宽 h + 起止角 θs/θe」共 6 个，
 *    其余成员（4 接合点、2 端帽圆心、中心弧中点、内外弧与端帽弧半径）全是派生量；
 * 2. `pointIds` 无序、拿不到角色，而派生量必须按角色绑定，因此显式输出全部结构成员；
 * 3. **起止角不做 2π 归一**：θe = θs + 有符号扫角，这样中心弧中点恒为 (θs+θe)/2，
 *    跨越 ±π 时也不会取到反侧；求解过程中两角连续变化也不会跳变。
 *
 * @param entity 草图实体（成员边）。
 * @returns 结构快照；非圆弧槽口成员返回 null。
 */
function createArcSlotStructureSnapshot(
    entity: ISketchObject,
): Readonly<Record<string, SerializablePlainData>> | null {
    const owner = Reflect.get(entity, 'closedCollection');

    if (!owner || typeof owner !== 'object' || Reflect.get(owner, 'isSketchSlotObject') !== true) {
        return null;
    }

    const slot = owner as {
        uuid: string;
        isArcSlot: () => boolean;
        junctionPoints: {p1: {uuid: string}; p2: {uuid: string}; p3: {uuid: string}; p4: {uuid: string}};
        arcCenterPoint: {uuid: string; getLocalPosition2D: () => {x: number; y: number}} | null;
        centerArcMidpoint: {uuid: string} | null;
        outerArc: {uuid: string; getArcDefinition: () => {radius: number; clockwise: boolean}} | null;
        innerArc: {uuid: string; getArcDefinition: () => {radius: number}} | null;
        startCapArc: {
            uuid: string;
            getCenterPoint: () => {uuid: string; getLocalPosition2D: () => {x: number; y: number}};
            getArcDefinition: () => {radius: number};
        };
        endCapArc: {
            uuid: string;
            getCenterPoint: () => {uuid: string; getLocalPosition2D: () => {x: number; y: number}};
        };
    };

    if (!slot.isArcSlot() || !slot.arcCenterPoint || !slot.centerArcMidpoint || !slot.outerArc || !slot.innerArc) {
        return null;
    }

    const center = slot.arcCenterPoint.getLocalPosition2D();
    const startCapCenter = slot.startCapArc.getCenterPoint().getLocalPosition2D();
    const endCapCenter = slot.endCapArc.getCenterPoint().getLocalPosition2D();
    const outerDefinition = slot.outerArc.getArcDefinition();
    const innerDefinition = slot.innerArc.getArcDefinition();
    const radius = (outerDefinition.radius + innerDefinition.radius) / 2;
    const halfWidth = slot.startCapArc.getArcDefinition().radius;

    if (!isFiniteNumber(radius) || radius <= 0 || !isFiniteNumber(halfWidth) || halfWidth <= 0) {
        return null;
    }

    const startAngle = Math.atan2(startCapCenter.y - center.y, startCapCenter.x - center.x);
    const rawEndAngle = Math.atan2(endCapCenter.y - center.y, endCapCenter.x - center.x);
    /** 有符号扫角：按外弧走向展开到 (0, 2π)，再带上方向符号，避免 ±π 处取反侧。 */
    let sweep = outerDefinition.clockwise ? startAngle - rawEndAngle : rawEndAngle - startAngle;

    sweep %= Math.PI * 2;
    if (sweep <= 0) {
        sweep += Math.PI * 2;
    }

    const junction = slot.junctionPoints;

    return {
        slotId: slot.uuid,
        junctionPointIds: [junction.p1.uuid, junction.p2.uuid, junction.p3.uuid, junction.p4.uuid],
        arcCenterPointId: slot.arcCenterPoint.uuid,
        centerArcMidpointId: slot.centerArcMidpoint.uuid,
        startCapCenterPointId: slot.startCapArc.getCenterPoint().uuid,
        endCapCenterPointId: slot.endCapArc.getCenterPoint().uuid,
        startCapArcId: slot.startCapArc.uuid,
        endCapArcId: slot.endCapArc.uuid,
        outerArcId: slot.outerArc.uuid,
        innerArcId: slot.innerArc.uuid,
        radius,
        halfWidth,
        startAngle,
        endAngle: startAngle + (outerDefinition.clockwise ? -sweep : sweep),
    };
}

/**
 * 创建草图实体状态。
 *
 * @param entity 草图实体。
 * @returns 实体状态。
 */
function createEdgeState(entity: ISketchObject): SketchEdgeState {
    const pointIds = readSketchEntityPointIds(entity);

    return {
        entityId: entity.uuid,
        entityKind: String(entity.objectType),
        pointIds,
        geometry: createEntityGeometrySnapshot(entity, pointIds),
        construction: readEntityConstructionState(entity),
    };
}

/**
 * 解析约束目标 ID。
 *
 * @param constraint solver 约束。
 * @returns 目标 ID。
 */
function resolveConstraintTargetIds(
    constraint: SketchRelationDefinition,
): {targetEntityIds: readonly string[]; targetPointIds: readonly string[]} {
    const targetEntityIds: string[] = [];
    const targetPointIds: string[] = [];

    constraint.getTargetsSnapshot().forEach((target) => {
        // 外部轴由稳定引用仓库恢复，不是参数 island 内的几何实体。
        if (isSketchMirrorAxisReferenceProvider(target)) {
            return;
        }
        if (isSketchPointObject(target)) {
            targetPointIds.push(target.uuid);
        }

        if (!isSketchPointObject(target) && Reflect.get(target, 'isSketchObject') === true) {
            const uuid = Reflect.get(target, 'uuid');
            if (typeof uuid === 'string') {
                targetEntityIds.push(uuid);
            }
        }

        if (isRuntimeSketchEdge(target)) {
            target.getRelatedSketchPointsSnapshot().forEach((point) => {
                targetPointIds.push(point.uuid);
            });
        }
    });

    return {
        targetEntityIds: sortUniqueStrings(targetEntityIds),
        targetPointIds: sortUniqueStrings(targetPointIds),
    };
}

/**
 * 创建约束状态。
 *
 * @param constraint solver 约束。
 * @returns 约束状态。
 */
/**
 * 读取约束的直接目标引用。
 *
 * 设计说明：
 * solver island 的 `targetEntityIds` / `targetPointIds` 还承担图遍历影响范围描述，会额外包含边关联点。
 * 约束 redo 重建必须使用这里记录的直接目标，才能恢复用户最初选择的线、圆、点等对象。
 *
 * @param constraint solver 约束。
 * @returns 直接目标引用列表。
 */
function resolveConstraintTargetRefs(constraint: SketchRelationDefinition): readonly SketchConstraintTargetRef[] {
    return constraint.getTargetsSnapshot()
        .map((target): SketchConstraintTargetRef | null => {
            if (isSketchMirrorAxisReferenceProvider(target)) {
                return {
                    targetKind: 'reference',
                    targetId: target.persistentId,
                };
            }
            if (isSketchPointObject(target)) {
                return {
                    targetKind: 'point',
                    targetId: target.uuid,
                };
            }

            if (Reflect.get(target, 'isSketchObject') === true) {
                const uuid = Reflect.get(target, 'uuid');
                if (typeof uuid === 'string' && uuid.length > 0) {
                    return {
                        targetKind: 'entity',
                        targetId: uuid,
                    };
                }
            }

            return null;
        })
        .filter((targetRef): targetRef is SketchConstraintTargetRef => targetRef !== null);
}

function createConstraintState(constraint: SketchRelationDefinition): SketchConstraintState {
    const targetIds = resolveConstraintTargetIds(constraint);
    const formulaPayload = constraint.createParametricFormulaPayload();
    const relationStates: readonly SketchRelationRuntimeState[] = constraint.isSuppressed
        ? ['suppressed']
        : constraint.isDangling
            ? ['dangling']
            : formulaPayload !== null && constraint.relationType === SketchRelationKind.OnEdge
                ? ['external']
                : constraint.relationType
                    ? ['satisfied']
                    : ['not-solved'];

    return {
        constraintId: resolveSketchRelationDefinitionHistoryId(constraint),
        runtimeId: constraint.id,
        relationKind: String(constraint.relationType),
        targetRefs: resolveConstraintTargetRefs(constraint),
        ...(() => {
            // 数值型关系的锁定量随快照下发，参数内核据此建方程（定义表是唯一事实源）。
            const numericValue = readSketchRelationNumericValue(constraint.relationType, constraint);

            return numericValue === null ? {} : {numericValue};
        })(),
        ...(formulaPayload === null ? {} : {formulaPayload}),
        ...(() => {
            const branchHint = constraint.getBranchHint();

            return branchHint === null ? {} : {branchHint};
        })(),
        targetEntityIds: targetIds.targetEntityIds,
        targetPointIds: targetIds.targetPointIds,
        states: relationStates,
    };
}

/**
 * 创建参数向量。
 *
 * @param pointStates 点状态。
 * @param dimensionStates 尺寸状态。
 * @returns 参数向量。
 */
function createParameterVector(
    pointStates: readonly SketchPointState[],
    dimensionStates: readonly SketchDimensionState[],
): SolverParameterVector {
    const pointEntries: SolverParameterEntry[] = pointStates.flatMap((pointState) => [
        {
            parameterId: `point:${pointState.pointId}:x`,
            value: pointState.localPosition[0],
            entityId: pointState.entityId,
            role: 'x',
        },
        {
            parameterId: `point:${pointState.pointId}:y`,
            value: pointState.localPosition[1],
            entityId: pointState.entityId,
            role: 'y',
        },
    ]);
    const dimensionEntries = dimensionStates
        .filter((dimensionState) => typeof dimensionState.parameterId === 'string')
        .map((dimensionState): SolverParameterEntry => ({
            parameterId: String(dimensionState.parameterId),
            value: dimensionState.value,
            dimensionId: dimensionState.dimensionId,
            role: dimensionState.drivingState,
        }));

    return {
        entries: [...pointEntries, ...dimensionEntries].sort((left, right) => {
            return left.parameterId.localeCompare(right.parameterId);
        }),
    };
}

/**
 * 筛选已解析 island 内的尺寸状态。
 *
 * @param dimensionStates 全量尺寸状态。
 * @param dimensionIds island 尺寸 ID。
 * @returns island 内尺寸状态。
 */
function filterDimensionStates(
    dimensionStates: readonly SketchDimensionState[],
    dimensionIds: readonly string[],
): readonly SketchDimensionState[] {
    const dimensionIdSet = new Set(dimensionIds);
    return dimensionStates.filter((dimensionState) => dimensionIdSet.has(dimensionState.dimensionId));
}

/**
 * 归一化 snapshot island ID。
 *
 * @param snapshot 原始 snapshot。
 * @param islandId 目标 island ID。
 * @returns 使用目标 island ID 的 snapshot。
 */
export function withSketchSolverRuntimeSnapshotIslandId(
    snapshot: SketchSolverIslandSnapshot,
    islandId: string,
): SketchSolverIslandSnapshot {
    return {
        ...snapshot,
        islandId,
    };
}

/**
 * 创建草图 solver island 运行时 snapshot。
 *
 * @param input 创建输入。
 * @returns snapshot 创建结果。
 */
export function createSketchSolverRuntimeSnapshot(
    input: CreateSketchSolverRuntimeSnapshotInput,
): SketchSolverRuntimeSnapshotResult {
    const graph = collectSketchSolverRuntimeGraph({
        seedObjects: input.seedObjects,
        supplementProvider: input.supplementProvider,
    });

    if (!graph) {
        return {
            snapshot: null,
            runtimePoints: [],
            runtimeEntities: [],
        };
    }

    const resolvedIsland = resolveSolverIsland({
        sketchId: graph.sketchId,
        seedEntityIds: graph.seedEntityIds,
        seedPointIds: graph.seedPointIds,
        explicitEntityIds: graph.seedEntityIds,
        explicitPointIds: graph.seedPointIds,
        entities: graph.entities,
        constraints: graph.constraints,
        dimensions: graph.dimensions,
        islandId: input.islandId,
    });
    const entityIdSet = new Set(resolvedIsland.entityIds);
    const constraintIdSet = new Set(resolvedIsland.constraintIds);
    const knownMemberIdSet = new Set([
        ...resolvedIsland.entityIds,
        ...resolvedIsland.pointIds,
        ...resolvedIsland.constraintIds,
        ...resolvedIsland.dimensionIds,
    ]);
    const dimensionStates = filterDimensionStates(graph.dimensionStates, resolvedIsland.dimensionIds);
    const runtimePoints = resolvedIsland.pointIds
        .map((pointId) => graph.pointObjects.get(pointId))
        .filter((point): point is SketchPointObject => Boolean(point));
    const pointStates = runtimePoints.map((point) => createPointState(point, graph));
    const runtimeEntities = resolvedIsland.entityIds
        .map((entityId) => graph.entityObjects.get(entityId))
        .filter((entity): entity is ISketchObject => Boolean(entity));
    const edgeStates = runtimeEntities.map(createEdgeState);
    const constraintStates = resolvedIsland.constraintIds
        .map((constraintId) => graph.constraintObjects.get(constraintId))
        .filter((constraint): constraint is SketchRelationDefinition => Boolean(constraint))
        .map(createConstraintState);
    const supplement = graph.supplement;
    const parameterVector = createParameterVector(pointStates, dimensionStates);
    const branchHints = (supplement?.branchHints ?? []).filter((branchHint) => {
        return branchHint.targetIds.every((targetId) => knownMemberIdSet.has(targetId));
    });
    const conflictStates = (supplement?.conflictStates ?? []).filter((conflictState) => {
        return conflictState.entityIds.every((entityId) => entityIdSet.has(entityId))
            && conflictState.constraintIds.every((constraintId) => constraintIdSet.has(constraintId))
            && conflictState.dimensionIds.every((dimensionId) => resolvedIsland.dimensionIds.includes(dimensionId));
    });

    return {
        runtimePoints,
        runtimeEntities,
        snapshot: {
            islandId: resolvedIsland.islandId,
            sketchId: resolvedIsland.sketchId,
            entityIds: resolvedIsland.entityIds,
            pointIds: resolvedIsland.pointIds,
            constraintIds: resolvedIsland.constraintIds,
            dimensionIds: resolvedIsland.dimensionIds,
            pointStates,
            edgeStates,
            constraintStates,
            dimensionStates,
            parameterVector,
            solveSeed: supplement?.solveSeed,
            branchHints,
            solveStatus: supplement?.solveStatus ?? DEFAULT_SOLVE_STATUS,
            conflictStates,
            lastStableParameterVector: conflictStates.length > 0 ? undefined : parameterVector,
            diagnostics: supplement?.diagnostics,
            capturedAt: input.capturedAt ?? Date.now(),
            revision: graph.sketchRevision,
        },
    };
}

/**
 * 创建 before/after union scope 使用的 island ID。
 *
 * @param before before snapshot。
 * @param after after snapshot。
 * @returns union scope island ID。
 */
export function createUnionSketchSolverIslandId(
    before: SketchSolverIslandSnapshot,
    after: SketchSolverIslandSnapshot,
): string {
    return createSketchSolverIslandId(before.sketchId, [
        ...before.entityIds,
        ...before.pointIds,
        ...before.constraintIds,
        ...before.dimensionIds,
        ...after.entityIds,
        ...after.pointIds,
        ...after.constraintIds,
        ...after.dimensionIds,
    ]);
}
